

import React, { createContext, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import type { MessageEvent, StatusEvent, default as PubNub } from 'pubnub';

/**
 * Campaign editing message payload structure
 */
export interface CampaignEditingMessage {
    campaignId: string;
    userId: string;
    fullName: string;
    updatedAt: string;
    screen: string | null;
}

/**
 * Campaign editing context state
 */
interface CampaignEditingContextState {
    /** List of campaigns currently being edited */
    editingCampaigns: CampaignEditingMessage[];
    /** Whether PubNub is connected */
    isConnected: boolean;
    /** Whether the current path matches valid URLs */
    isValidPath: boolean;
}

/**
 * Campaign editing context type
 */
interface CampaignEditingContextType extends CampaignEditingContextState {
}

const CampaignEditingContext = createContext<CampaignEditingContextType | undefined>(undefined);

/**
 * Valid URL patterns that should connect to the campaign editing channel
 */
const VALID_URL_PATTERNS = [
    /^\/campaigns\/edit\/(revcontent|outbrain|mediago|google|mgid)\/[^/]+/,
    /^\/rotations\/[^/]+/,
    /^\/dashboard/,
];

const CHANNEL_NAME = 'clickster-campaign-editing';
const ENV_PREFIX = process.env.NEXT_PUBLIC_ENVIRONMENT === 'production' ? 'p-' : 's-';

/**
 * Props for the CampaignEditingProvider component
 */
interface CampaignEditingProviderProps {
    children: React.ReactNode;
}

/**
 * Provider component for campaign editing context
 * Connects to PubNub when on valid campaign editing paths
 */
export function CampaignEditingProvider({ children }: CampaignEditingProviderProps) {
    const pathname = usePathname();
    const [editingCampaigns, setEditingCampaigns] = useState<CampaignEditingMessage[]>([]);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const pubnubRef = useRef<PubNub | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const mounted = useRef<boolean>(false);
    const isValidPath = useMemo(() => VALID_URL_PATTERNS.some(pattern => pattern.test(pathname)), [pathname]);

    // Don't initialize PubNub on the server side
    const isClient = typeof window !== 'undefined';

    // Helper to clean up pubnub connection
    const cleanupPubNub = () => {
        if (pubnubRef.current) {
            pubnubRef.current.unsubscribeAll();
            pubnubRef.current.removeListener({});
            pubnubRef.current = null;
            setIsConnected(false);
            setEditingCampaigns([]); // Clear the list when unsubscribing
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    };

    useEffect(() => {
        cleanupPubNub();
    }, [isValidPath]);

    useEffect(() => {
        mounted.current = true;

        // Don't initialize PubNub on the server side or invalid paths
        if (!isClient || !isValidPath) {
            return;
        }

        const initPubNub = async () => {
            // Don't initialize PubNub if already initialized
            if (pubnubRef.current) {
                return;
            }

            try {
                const { default: PubNubManager } = await import('../utils/pubnub');
                if (!mounted.current) return;

                console.log(`[CampaignEditing] ${pathname} Initializing PubNub`);
                // Generate unique userId per tab (same pattern as CampaignEditingService)
                const userId = localStorage.getItem('clk-u') || 'unknown-user';
                const tabId = Math.random().toString(36).substring(2, 8);
                const uniqueUserId = `${userId}-tab-${tabId}`;

                const manager = new PubNubManager(uniqueUserId);
                const pubnubInstance = manager.getPubNubInstance();

                if (!pubnubInstance) {
                    console.error('[CampaignEditing] Failed to get PubNub instance');
                    return;
                }

                pubnubRef.current = pubnubInstance;
                const fullChannelName = `${ENV_PREFIX}${CHANNEL_NAME}`;

                const reconnect = () => {
                    cleanupPubNub();
                    // Debounce reconnects, reconnect after 1 second
                    reconnectTimeoutRef.current = setTimeout(() => {
                        if (mounted.current) {
                            console.log(`[CampaignEditing] ${pathname} Attempting PubNub reconnect after PNNetworkUpCategory`);
                            initPubNub();
                        }
                    }, 1000);
                };

                const listener = {
                    status: (statusEvent: StatusEvent) => {
                        if (statusEvent?.category === 'PNConnectedCategory') {
                            console.log(`[CampaignEditing] ${pathname} PubNub Connected`);
                            setIsConnected(true);
                        } else if (
                            statusEvent?.category === 'PNDisconnectedCategory' ||
                            statusEvent?.category === 'PNNetworkDownCategory'
                        ) {
                            console.log(`[CampaignEditing] ${pathname} PubNub Disconnected`);
                            setIsConnected(false);
                        } else if (statusEvent?.category === 'PNNetworkUpCategory') {
                            console.log(`[CampaignEditing] ${pathname} PNNetworkUpCategory detected, reconnecting...`);
                            reconnect();
                        }
                    },
                    message: (messageEvent: MessageEvent) => {
                        const message = messageEvent.message as CampaignEditingMessage;
                        console.log(`[CampaignEditing] ${pathname} Received message:`, message);

                        if (mounted.current) {
                            // Don't add messages from the current user or invalid messages
                            const currentUserId = localStorage.getItem('clk-u') || 'unknown-user';
                            if (message.userId !== currentUserId && message.campaignId && message.userId) {
                                setEditingCampaigns(prev => [...prev, message]);
                            }
                        }
                    },
                };

                pubnubInstance.addListener(listener);
                pubnubInstance.subscribe({ channels: [fullChannelName] });

                console.log(`[CampaignEditing] ${pathname} Subscribed to channel: ${fullChannelName} with userId: ${uniqueUserId}`);

            } catch (error) {
                console.error(`[CampaignEditing] ${pathname} Failed to initialize PubNub:`, error);
            }
        };

        initPubNub();

        return () => {
            mounted.current = false;
            cleanupPubNub();
        };
    }, [pathname, isValidPath, isClient, pubnubRef, reconnectTimeoutRef, mounted]);

    const contextValue: CampaignEditingContextType = {
        editingCampaigns,
        isConnected,
        isValidPath,
    };

    return (
        <CampaignEditingContext.Provider value={contextValue}>
            {children}
        </CampaignEditingContext.Provider>
    );
}

/**
 * Hook to use the campaign editing context
 * @returns The campaign editing context
 * @throws Error if used outside of CampaignEditingProvider
 */
export function useCampaignEditing(): CampaignEditingContextType {
    const context = useContext(CampaignEditingContext);

    if (context === undefined) {
        throw new Error('useCampaignEditing must be used within a CampaignEditingProvider');
    }

    return context;
}
